/**
 * Single source of truth for legal / payment-compliance details.
 *
 * These values appear on the checkout dialog, footer and legal pages to satisfy
 * the card-scheme / acquirer (Lipad) website compliance requirements:
 *   1. Card brand marks            -> PaymentCompliance.vue
 *   2. Terms incl. Refund Policy   -> TermsConditionsPage / RefundPolicyPage
 *   3. Privacy Policy              -> PrivacyPolicyPage
 *   4. Establishment address+country at checkout -> establishment.*
 *   5. Transaction currency        -> payments.currency
 *   6. Legal restrictions          -> TermsConditionsPage (Eligibility & Restrictions)
 *   7. Card data security policy   -> PrivacyPolicyPage (Payment Card Security)
 *   8. Descriptor + Payment Facilitator on payments page -> payments.*
 *
 * ⚠️ FILL IN the values marked `TODO` with your real, registered details before
 * going live — a wrong address or descriptor will fail the Lipad review.
 */
export const company = {
  /** Public trading / brand name. */
  tradingName: "GoPlayTV",

  /** Registered legal entity name (as on your incorporation documents). */
  legalName: "GoPlayTV Ltd", // TODO: confirm exact registered company name

  /** Company / business registration number, if applicable. */
  registrationNumber: "", // TODO: e.g. "PVT-XXXXXXX" (optional but recommended)

  /**
   * Permanent establishment address — shown at checkout (requirement #4).
   * The outlet COUNTRY must be present.
   */
  establishment: {
    line1: "TODO: Street address",
    line2: "",
    city: "TODO: City",
    region: "", // state / province / county
    postalCode: "",
    country: "TODO: Country", // e.g. "Kenya" — the outlet country (required)
    countryCode: "", // ISO-2, e.g. "KE"
  },

  /** Customer-facing support contacts. */
  support: {
    email: "support@goplaytv.live", // TODO: confirm
    phone: "", // TODO: e.g. "+254 ..."
    hours: "24/7",
  },

  payments: {
    /**
     * Transaction currency the customer is actually charged in (requirement #5).
     * Must match the Lipad service currency the card is processed in.
     */
    currency: "USD",

    /**
     * Billing/statement descriptor — exactly how the charge appears on the
     * cardholder's statement (requirement #8). Leave blank until Lipad confirms
     * the exact descriptor; a blank value is hidden rather than guessed.
     */
    descriptor: "", // TODO: set the exact descriptor Lipad uses, e.g. "GOPLAYTV*IPTV"

    /** Payment Facilitator / processor shown on the payments page (requirement #8). */
    facilitator: {
      name: "Lipad",
      // Public note rendered near the descriptor.
      note: "Card payments are securely processed by Lipad, a licensed payment facilitator.",
    },

    /** Card schemes you accept (drives which brand marks render). */
    schemes: ["visa", "mastercard"] as Array<"visa" | "mastercard" | "amex">,
  },

  /** Governing law / jurisdiction for the Terms. */
  jurisdiction: "TODO: Country/State of governing law",

  /** Refund policy summary (full text on the Refund page). */
  refund: {
    // Window, in days, during which a refund may be requested.
    windowDays: 7,
  },

  /** Minimum customer age (legal restriction, requirement #6). */
  minAge: 18,
};

/** True while a value is still an unfilled placeholder. */
function unset(v: string): boolean {
  return !v || /^TODO/i.test(v.trim());
}

/**
 * Convenience: one-line establishment address with country.
 * Returns "" until real values are filled in (placeholders are dropped) so
 * customers never see "TODO" on the checkout/legal pages.
 */
export function establishmentLine(): string {
  const e = company.establishment;
  return [e.line1, e.line2, e.city, e.region, e.postalCode, e.country]
    .filter((v) => v && !unset(v))
    .join(", ");
}
